// src/hooks/pages/useAdministrators.ts
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '@/store';
import type { RootState } from '@/store/rootReducer';
import type { AdminUser } from '@/types/organization.types';
import { fetchOrgAdmins } from '@/store/thunks/organization/fetchOrgAdmins';
import { createAdminThunk } from '@/store/thunks/administratorsForm/createAdminThunk';
import { updateAdminThunk } from '@/store/thunks/administratorsForm/updateAdminThunk';
import { useModal } from '@/hooks/useModal';
import { useAdminDialogs } from '@/hooks/useAdminDialogs';

const MIN_SEARCH_LENGTH = 0;

export function useAdministrators() {
  const dispatch = useAppDispatch();

  // dialogs & modal hooks
  const { modal, close: closeModal, isType } = useModal();
  const {
    openCreateAdmin,
    openEditAdmin,
    confirmToggleActive,
    confirmForceLogout,
    confirmForceLoginReset,
    confirmDelete,
  } = useAdminDialogs();

  // store selectors
  const admins  = useSelector((state: RootState) => state.organization.data?.administrators ?? []);
  const loading = useSelector((state: RootState) => state.organization.loading.admins);

  // local UI (presentation) state
  const [search, setSearch] = useState<string>('');

  // fetch on mount
  useEffect(() => { void dispatch(fetchOrgAdmins()); }, [dispatch]);

  // modal state + selected admin
  const isCreate = isType('createAdmin');
  const isEdit   = isType('editAdmin');
  const editingAdmin = (isEdit && (modal !== null) && 'admin' in modal ? (modal.admin) : undefined);

  // handlers (presentation-friendly)
  const handleOpenCreate = useCallback(() => {
    openCreateAdmin();
  }, [openCreateAdmin]);

  const handleEdit = useCallback((row: AdminUser) => {
    openEditAdmin(row);
  }, [openEditAdmin]);

  const handleToggleActive = useCallback((row: AdminUser) => {
    confirmToggleActive(row);
  }, [confirmToggleActive]);

  const handleForceLogout = useCallback((row: AdminUser) => {
    confirmForceLogout(row);
  }, [confirmForceLogout]);

  const handleForceLoginReset = useCallback((row: AdminUser) => {
    confirmForceLoginReset(row);
  }, [confirmForceLoginReset]);

  const handleDelete = useCallback((row: AdminUser) => {
    confirmDelete(row);
  }, [confirmDelete]);

  const filteredAdmins = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (query.length === MIN_SEARCH_LENGTH) return admins;

    return admins.filter((admin) => {
      const haystack = [admin.name, admin.email, admin.phone, admin.status]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [admins, search]);

  const handleSubmit = useCallback(
    async(values: { name: string; email: string; phone: string }) => {
      try {
        if (isCreate) {
          await dispatch(createAdminThunk(values)).unwrap();
        } else if (isEdit) {
          await dispatch(updateAdminThunk({ id: editingAdmin?.id ?? '', data: values })).unwrap();
        }
        void dispatch(fetchOrgAdmins());
        closeModal();
      } catch {
      }
    },
    [dispatch, isCreate, isEdit, editingAdmin, closeModal]
  );

  return {
    // data
    rows: filteredAdmins,
    loading,

    // header props
    search,
    setSearch,
    handleOpenCreate,

    // table handlers
    handleEdit,
    handleToggleActive,
    handleForceLogout,
    handleForceLoginReset,
    handleDelete,

    // modal props
    isCreate,
    isEdit,
    editingAdmin,
    handleSubmit,
    closeModal,
  };
}
